import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AppLauncher } from '@capacitor/app-launcher';
import { AlertController, IonicModule, MenuController, ModalController, Platform } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ComponentsModule } from 'src/components/components.module';
import { AppStoreService } from 'src/services/app-store.service';
import { GymService } from 'src/services/gym.service';
import { AVAILABLE_LANGS, LANGUAGE_DISPLAY_NAMES, TranslationService } from 'src/services/translation.service';
import { ActiveGymFacade } from 'src/store/active-gym/active-gym.facade';
import { WallResetPage } from '../../pages/wall-reset/wall-reset.page';
import { WallsPage } from '../../pages/walls/walls.page';
import { AuthService } from '../../services/auth.service';
import { GuestService } from '../../services/guest-service';
import { RestService } from '../../services/rest.service';
import { BBStorage } from '../../utils/bb-storage';
import { ContactGymAdminComponent } from '../contact-gym-admin-dialog/contact-gym-admin.component';
import { DonateComponent } from '../donate/donate.component';
import { FeedbackComponent } from '../feedback/feedback.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    TranslateModule,
    ComponentsModule,
    RouterModule,
    DonateComponent
  ],
  selector: 'main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
})
export class MainMenuComponent implements OnInit {

  gradeSystem: string;
  alwaysRebuildCacheFlag = false;
  username: string;
  private titleBeingPressed = false;
  selectedLanguage;
  availableLanguages;
  displayNameByLanguage = LANGUAGE_DISPLAY_NAMES;
  allTime: boolean = false;
  isBetaTester: boolean = false;

  constructor(
    private menuCtrl: MenuController,
    private platform: Platform,
    private translationService: TranslationService,
    private router: Router,
    private appStoreService: AppStoreService,
    public guestService: GuestService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private translateService: TranslateService,
    public bbStorage: BBStorage,
    private restService: RestService,
    private gymFacade: ActiveGymFacade,
    private gymService: GymService,
    public authService: AuthService) {
  }

  ngOnInit() {
    this.gradeSystem = this.bbStorage.gradeSystemId;
    this.alwaysRebuildCacheFlag = this.bbStorage.alwaysRebuildEntireBoulderCache;
    this.username = this.authService.getCurrentUsername();
    this.selectedLanguage = this.translationService.getCurrentLanguage();
    this.availableLanguages = AVAILABLE_LANGS;
    this.allTime = this.bbStorage.allTimeModeActive;
    this.authService.isBetaTester().then(b => {
      this.isBetaTester = b;
    });
  }

  public openBoulderBookShop() {
    window.open('https://shop.boulder-book.com', '_system', 'location=yes');
  }

  public async openInstagram() {
    if (this.platform.is('mobile')) {
      const canOpen = await AppLauncher.canOpenUrl({ url: this.platform.is('android') ? 'com.instagram.android' : 'instagram://' });
      if (canOpen.value) {
        await AppLauncher.openUrl({ url: 'instagram://user?username=boulder.book' });
      } else {
        this.openInstagramInBrowser();
      }
    } else {
      this.openInstagramInBrowser();
    }
  }

  private openInstagramInBrowser() {
    window.open('https://www.instagram.com/boulder.book', '_system', 'location = yes');
  }

  async allTimeToggleChanged() {
    if (this.allTime) {
      const alert = await this.alertCtrl.create({
        header: this.translateService.instant('main-menu.all-time-mode'),
        message: this.translateService.instant('main-menu.all-time-mode-warning'),
        cssClass: 'bb-alert',
        buttons: [
          {
            text: this.translateService.instant('shared.cancel'),
            role: 'cancel',
            cssClass: 'cancel-button',
            handler: () => {
              this.allTime = !this.allTime;
            }
          },
          {
            text: this.translateService.instant('app-component.yes'),
            cssClass: 'yes-button',
            handler: async () => {
              await this.updateAllTimeFlagResyncAndClose();
            }
          }
        ]
      });
      await alert.present();
    } else {
      await this.updateAllTimeFlagResyncAndClose();
    }
  }

  private async updateAllTimeFlagResyncAndClose() {
    this.bbStorage.allTimeModeActive = this.allTime;
    await this.gymService.resyncActiveGym();
    await this.menuCtrl.close('boulder-list-menu');
  }

  async logout() {
    await this.bbStorage.clear();
    await this.authService.logout();
    await this.bbStorage.initialize();
    await this.restService.updateSchema();
    await this.router.navigate(['login']);
  }

  async showFeedbackModal() {
    const modal = await this.modalCtrl.create({
      id: 'feedback-modal',
      component: FeedbackComponent
    });
    return await modal.present();
  }

  async manageWalls() {
    if (!(await this.isGymAdmin())) {
      await this.showContactGymAdminDialog();
      return;
    }
    const modal = await this.modalCtrl.create({
      component: WallsPage,
      id: 'WallsPageModal'
    }
    );
    await modal.present();
  }

  private async isGymAdmin(): Promise<boolean> {
    await this.gymService.syncActiveGym();
    const activeGym = await firstValueFrom(this.gymFacade.activeGym$);
    const gym = await this.gymService.getGymListEntryFromPouch$(activeGym.gymId);
    return this.authService.isGymAdmin(gym);
  }

  async donateButtonClicked() {
    const modal = await this.modalCtrl.create({
      component: DonateComponent
    }
    );
    await modal.present();
  }

  async reset() {
    if (!(await this.isGymAdmin())) {
      await this.showContactGymAdminDialog();
      return;
    }
    const modal = await this.modalCtrl.create({
      component: WallResetPage,
      id: 'WallResetPage'
    });
    modal.onDidDismiss().then(async () => {
      await this.menuCtrl.close();
    });
    await modal.present();
  }

  alwaysRebuildCacheChanged(value) {
    this.bbStorage.setAlwaysRebuildEntireBoulderCache(value);
  }

  selectedLanguageChanged() {
    this.translationService.setCurrentLanguage(this.selectedLanguage);
  }

  public showDonateButton() {
    return this.platform.is('capacitor') && this.appStoreService.getProducts()().length > 0;
  }

  private async showContactGymAdminDialog() {
    const modal = await this.modalCtrl.create({
      component: ContactGymAdminComponent
    });
    await modal.present();
  }

  async deleteAccount() {
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant('settings-page.ask-delete-account'),
      buttons: [
        {
          text: this.translateService.instant('shared.cancel'),
          role: 'cancel',
          cssClass: 'secondary'
        }, {
          text: this.translateService.instant('settings-page.yes-please-delete'),
          handler: async () => {
            await this.restService.deleteAccount();
            await this.logout();
          }
        }
      ]
    });
    await alert.present();
  }

  gradeSystemChanged($event) {
    this.bbStorage.gradeSystemId = this.gradeSystem;
  }

  titlePressed() {
    this.titleBeingPressed = true;
    setTimeout(async () => {
      if (this.titleBeingPressed) {
        this.bbStorage.setBetaMode(true);
      }
    }, 5000);
  }

  titleReleased() {
    this.titleBeingPressed = false;
  }
}
